import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import SeoMeta from "@/components/feature/SeoMeta";
import SectionHeading from "@/components/feature/SectionHeading";
import { siteInfo } from "@/mocks/site";
import { voices } from "@/mocks/voices";

const CONTACT_URL = "https://readdy.ai/api/form/dah959bp14a1h3mr6fsg";

const voiceAccent: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-800",
  secondary: "bg-secondary-100 text-secondary-800",
};

export default function About() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (window.location.hash === "#contact") {
      const el = document.getElementById("contact");
      if (el) {
        window.setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      }
    }
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("contact_alt");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("sending");
    setFormError("");

    try {
      const response = await fetch(CONTACT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const succeeded = response.ok && parsed?.code === "OK";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!succeeded || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <>
      <SeoMeta
        title="关于我们 - 独家大瓜内容原则与联系方式"
        description="了解独家大瓜的网站定位、发展理念与内容原则：只做首发追踪、对信息做可信度分级、涉及具体个人一律化名。可通过表单或邮箱与我们联系与投稿。"
        keywords="独家大瓜,关于我们,内容原则,爆料投稿,独家大瓜首发"
        path="/about"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于独家大瓜",
            description: siteInfo.intro,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "联系独家大瓜",
            inLanguage: "zh-CN",
          },
        ]}
      />

      <nav aria-label="面包屑" className="border-b border-background-200 bg-background-100">
        <ol className="shell flex flex-wrap items-center gap-2 px-4 py-3 text-xs text-foreground-500 md:px-6">
          <li>
            <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
              首页
            </Link>
          </li>
          <li aria-hidden="true">
            <i className="ri-arrow-right-s-line" />
          </li>
          <li className="font-medium text-foreground-800">关于我们</li>
        </ol>
      </nav>

      <section className="bg-secondary-900 px-4 py-14 md:px-6 md:py-20">
        <div className="shell">
          <span className="text-xs font-label font-bold uppercase tracking-[0.25em] text-accent-300">
            About Us
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
            把一条热搜，
            <br className="hidden sm:block" />
            还原成一条看得懂的时间线
          </h1>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">{siteInfo.intro}</p>
          <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-4">
            {siteInfo.stats.map((stat) => (
              <div key={stat.label}>
                <span className="block font-heading text-2xl font-black text-accent-300 md:text-3xl">
                  {stat.value}
                </span>
                <span className="mt-1 block text-xs text-background-300">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 px-4 py-12 md:px-6 md:py-16">
        <div className="shell">
        <SectionHeading
          eyebrow="Principles"
          title="我们的内容原则"
          desc="四条硬规矩，决定了我们能写什么、不写什么。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {siteInfo.principles.map((item) => (
            <div
              key={item.title}
              className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300 md:p-6"
            >
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md bg-primary-100 text-xl text-primary-600">
                <i className={item.icon} aria-hidden="true" />
              </span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        </div>
      </section>

      <section className="bg-background-100 px-4 py-12 md:px-6 md:py-16">
        <div className="shell">
        <SectionHeading
          eyebrow="Voices"
          title="读者怎么说"
          desc="来自长期读者的真实反馈，我们只挑了几条有代表性的。"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {voices.map((voice) => (
            <figure
              key={voice.name}
              className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <i className="ri-double-quotes-l text-2xl text-primary-300" aria-hidden="true" />
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                {voice.text}
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-full font-heading text-sm font-black ${
                    voiceAccent[voice.color] ?? voiceAccent.primary
                  }`}
                >
                  {voice.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block text-sm font-bold text-foreground-950">{voice.name}</span>
                  <span className="block text-[11px] text-foreground-500">{voice.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
        </div>
      </section>

      <section id="contact" className="scroll-mt-28 bg-background-50 px-4 py-12 md:px-6 md:py-16">
        <div className="shell">
        <SectionHeading
          eyebrow="Contact"
          title="联系我们与投稿"
          desc="有线索想提供，或者想聊聊合作，都可以通过下面的方式找到我们。"
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-10">
          <div className="lg:col-span-2">
            <ul className="flex flex-col gap-4">
              {siteInfo.contactChannels.map((channel) => (
                <li
                  key={channel.label}
                  className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md bg-accent-100 text-xl text-accent-700">
                    <i className={channel.icon} aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs font-label font-bold text-foreground-500">{channel.label}</span>
                    <span className="mt-1 block text-sm font-medium text-foreground-900">{channel.value}</span>
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
                <i className="ri-shield-keyhole-line text-primary-500" aria-hidden="true" />
                投稿说明
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                我们会对线索来源做保密处理，并在发布前进行交叉核实。无法独立验证的部分会在正文中明确标注，不会当作事实发布。
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <form
              id="contact-form"
              data-readdy-form
              action={CONTACT_URL}
              method="post"
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-sm font-label font-bold text-foreground-800">
                    称呼 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-sm font-label font-bold text-foreground-800">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-subject" className="mb-2 block text-sm font-label font-bold text-foreground-800">
                    联系类型
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    defaultValue="提供爆料线索"
                    className="h-11 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="提供爆料线索">提供爆料线索</option>
                    <option value="内容纠错与更正">内容纠错与更正</option>
                    <option value="媒体与内容合作">媒体与内容合作</option>
                    <option value="其他咨询">其他咨询</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="mb-2 block text-sm font-label font-bold text-foreground-800">
                    具体内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="请尽量写清楚时间、经过和你能提供的依据（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm leading-relaxed outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                  <p className="mt-1.5 text-xs text-foreground-500">最多 500 字，超出将无法提交。</p>
                </div>
              </div>

              <input
                type="text"
                name="contact_alt"
                className="contact-note-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-label font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "sending" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                      提交中
                    </>
                  ) : (
                    <>
                      提交留言
                      <i className="ri-send-plane-line" aria-hidden="true" />
                    </>
                  )}
                </button>
                {status === "success" && (
                  <p className="flex items-center gap-2 text-sm text-accent-800">
                    <i className="ri-checkbox-circle-fill" aria-hidden="true" />
                    已收到你的留言，我们会尽快查看并回复。
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-2 text-sm text-primary-700">
                    <i className="ri-error-warning-fill" aria-hidden="true" />
                    {formError}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
        </div>
      </section>
    </>
  );
}